/* eslint-disable no-console */
/**
 * Extracts uuid, id (origin) & slug from provided URL section, e.g.:
 *
 * const test1 = 'b697d0d6-5b53-11eb-bbff-0242ac120035'
 * slugParser(test1)
 *
 *  {
 *      url: 'b697d0d6-5b53-11eb-bbff-0242ac120035',
 *      id: undefined,
 *      uuid: 'b697d0d6-5b53-11eb-bbff-0242ac120035',
 *      slug: undefined
 *  }
 *
 *
 * const test2 = 'pizza-tijesto-b697d0d6-5b53-11eb-bbff-0242ac120035'
 * slugParser(test2)
 *
 *  {
 *      url: 'pizza-tijesto-b697d0d6-5b53-11eb-bbff-0242ac120035',
 *      id: undefined,
 *      uuid: 'b697d0d6-5b53-11eb-bbff-0242ac120035',
 *      slug: 'pizza-tijesto'
 *  }
 *
 *
 * const test3 = '1108749'
 * slugParser(test3)
 *
 *  {
 *      url: '1108749',
 *      id: '1108749',
 *      uuid: undefined,
 *      slug: undefined
 *  }
 *
 *
 * const test4 = 'pizza-tijesto'
 * slugParser(test4)
 *
 *  {
 *      url: 'pizza-tijesto',
 *      id: undefined,
 *      uuid: undefined,
 *      slug: 'pizza-tijesto'
 *  }
 *
 *
 * const test5 = 'pizza-tijesto-1108749'
 * slugParser(test5)
 *
 *  {
 *      url: 'pizza-tijesto-1108749',
 *      id: '1108749',
 *      uuid: undefined,
 *      slug: 'pizza-tijesto'
 *  }
 *
 * const test6 = { slug: 'pizza-tijesto-b697d0d6-5b53-11eb-bbff-0242ac120035' }
 * slugParser(test6)
 *
 *  {
 *      url: 'pizza-tijesto-b697d0d6-5b53-11eb-bbff-0242ac120035',
 *      id: undefined,
 *      uuid: 'b697d0d6-5b53-11eb-bbff-0242ac120035',
 *      slug: 'pizza-tijesto'
 *  }
 *
 * Every other invalid case will return an empty Object, so we don't break the app and we can redirect within the route instead.
 *
 * @param {*} slug
 */
export const slugParser = (slugOrParams) => {
    let slugToTest = slugOrParams;

    try {
        // For better DX, we can directly pass params or query, instead of destructuring it into slug.
        if (
            typeof slugOrParams === "object" &&
            Object.keys(slugOrParams || {})?.length &&
            (slugOrParams?.slug || slugOrParams?.category)
        ) {
            slugToTest = slugOrParams.slug || slugOrParams?.category;
        } else if (typeof slugOrParams === "object" && !slugOrParams?.slug) {
            console.error(
                `[slugParser] Invalid params or query of '${JSON.stringify(
                    slugOrParams,
                )}' provided, missing 'slug' or 'category' property.`,
            );
            return {};
        } else if (!slugOrParams || typeof slugOrParams !== "string" || !slugOrParams?.length) {
            console.error(
                // eslint-disable-next-line max-len
                `[slugParser] Invalid slug '${slugOrParams}' provided, please only use strings. Also, slugs cannot be empty.`,
            );

            return {};
        }

        // Used to extract UUID of any kind from any string, e.g. 'pizza-tijesto-b697d0d6-5b53-11eb-bbff-0242ac1200350'
        const uuidTest = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

        // Test if string contains only numeric characters, e.g. '11087490
        const idTest = /\d+/;

        /**
         * Removes trailing dashes when non-slug patterns are removed, e.g.
         *
         *  'pizza-tijesto-' becomes 'pizza-tijesto'
         *
         * @param {*} str
         */
        const removeTrailingDash = (str) => str.substr(0, str.lastIndexOf("-", str.length - 1));

        /**
         * Extracts pattern from provided string, based on provided pattern, e.g.:
         *
         *  const test = 'pizza-tijesto-b697d0d6-5b53-11eb-bbff-0242ac120035'
         *  const onlyUuid = getPattern(text, uuidTest)
         *
         *  console.log(onlyUuid) // 'b697d0d6-5b53-11eb-bbff-0242ac120035'
         *
         * @param {*} str
         * @param {*} patternTest
         * @return {*}
         */
        const getPattern = (str, patternTest) => {
            const maybePattern = str.match(patternTest);

            if (maybePattern && Array.isArray(maybePattern) && maybePattern.length) {
                return maybePattern[0];
            }

            return undefined;
        };

        let maybeHasV1Uuid = getPattern(slugToTest, uuidTest);
        let maybeHasId;
        let maybeSlug;

        // Since we can never have both ID & UUID in our URL, we test for UUID first or we might get false positives for numbers within UUID.
        if (maybeHasV1Uuid) {
            // Avoid slug mutation
            const newSlug = slugToTest;
            maybeSlug = newSlug.replace(maybeHasV1Uuid, "");

            // Because slugs are not unique, it's possible to have slug as UUID, match slug again for UUID to cover that case
            const maybeHasV1UuidAgain = getPattern(maybeSlug, uuidTest);
            if (maybeHasV1UuidAgain) {
                maybeHasV1Uuid = maybeHasV1UuidAgain;
                maybeSlug = newSlug.replace(maybeHasV1Uuid, "");
            }
        } else {
            maybeHasId = getPattern(slugToTest, idTest);
            const maybeItIsId = getPattern(slugToTest, idTest);

            if (parseInt(maybeItIsId, 10) <= 10000) {
                maybeHasId = slugToTest;
            } else {
                maybeHasId = getPattern(slugToTest, idTest);
            }
        }

        if (maybeHasId) {
            // Avoid slug mutation
            const newSlug = slugToTest;
            maybeSlug = newSlug.replace(maybeHasId, "");
        }

        if (maybeSlug) {
            maybeSlug = removeTrailingDash(maybeSlug);
        }

        if (!maybeHasV1Uuid && !maybeHasId) {
            maybeSlug = slugToTest;
        }

        return {
            url: slugToTest,
            id: maybeHasId,
            uuid: maybeHasV1Uuid,
            slug: maybeSlug?.length ? maybeSlug : undefined,
        };
    } catch (error) {
        console.error("[slugParser]", error);
        // Instead of throwing error, we return an empty object to let the routing mechanism decide what to do.
        return {};
    }
};
